<template>
  <section
    id="project-sidebar"
    :class="{
      opened: isSidebarOpen || isCreateMode,
      closed: !isSidebarOpen && !isCreateMode,
      'edit-mode': !isCreateMode,
      'create-mode': isCreateMode,
      'z-index-full': zIndex,
    }"
  >
    <article class="sidebar-header" v-if="!isCreateMode">
      <p v-if="isSidebarOpen && !isCreateMode" class="sidebar-title">
        Edit {{ typeOfProjectDisplay }}
      </p>
      <button
        v-if="!isCreateMode"
        id="toggle-sidebar"
        @click="toggleSidebar"
        :class="{ close: !isSidebarOpen }"
      >
        <img src="@/assets/icons/ui/circled-arrow.png" alt="open sidebar" />
      </button>
    </article>

    <!-- Open -->
    <template v-if="isSidebarOpen">
      <!-- Sales rep -->
      <article id="sales-rep" v-if="dealerMode">
        <label
          for="edit-sales-rep"
          class="input-container"
          :class="{ error: errors.rep }"
        >
          <div class="sidebar-option-title">
            <span>Sales Rep</span>
            <span class="grey">(Search or Add New )</span>
            <InformationHover
              :description="'All messages and quote requests for this project will go to Sales Rep email. If a store is created, it will be associated with this user as well.'"
            />
          </div>
          <v-select
            class="input-style select"
            v-model="selectedRepId"
            @input="
              (newRepId) => {
                debounceUpdate({
                  dbKey: 'rep_id',
                  field: 'repId',
                  value: Number(newRepId),
                });
              }
            "
            :options="reps"
            :reduce="(rep) => rep.id"
            :clearable="false"
            label="name"
            placeholder="Select a sales rep"
            @search="
              (search) => {
                if (search) repsDropdownSearch = search;
              }
            "
            @search:focus="repsDropdownSearch = ''"
          >
            <!-- Custom option to add a new rep -->

            <template #list-header>
              <li class="new-rep-option">
                <button
                  @click="showCreateSalesRepModal = true"
                  class="btn flex justify-content-between"
                >
                  <span class="mr-2">
                    {{
                      repsDropdownSearch
                        ? `Add New Sales Rep called "${repsDropdownSearch}"`
                        : "Add New Sales Rep"
                    }}
                  </span>
                  <img
                    src="@/assets/icons/ui/add-circle-outline-black.svg"
                    alt="add icon"
                  />
                </button>
              </li>
            </template>
          </v-select>

          <span class="error-msg" v-if="errors.rep">Missing Sales rep</span>
        </label>
      </article>

      <!-- Project Title -->
      <article id="project-title" v-if="dealerMode">
        <label
          for="edit-project-title"
          class="input-container"
          :class="{ error: errors.title }"
        >
          <div class="sidebar-option-title">
            <span>{{ typeOfProjectDisplay }} Title</span>
          </div>
          <input
            id="edit-project-title"
            class="input-style"
            type="text"
            placeholder='e.g "Jamie Scott Fitness" or "2020 Polo Options"  '
            v-model="title"
            @input="
              debounceUpdate({
                dbKey: 'title',
                field: 'title',
                value: $event.target.value,
              })
            "
          />
          <span class="error-msg" v-if="errors.title"
            >Missing {{ typeOfProjectDisplay }} title</span
          >
        </label>
      </article>

      <!-- School search bar -->
      <article id="customer-type">
        <div class="input-group customer-type-dropdown">
          <label class="sidebar-option-title">Customer Type</label>
          <v-select
            class="input-style select"
            id="institution-select"
            label="Select an institution"
            :clearable="false"
            :options="institutions"
            v-model="selectedCustomerType"
            @input="
              (newCustomer) => {
                debounceUpdate({
                  dbKey: 'customer_type',
                  field: 'customerType',
                  value: newCustomer,
                });
              }
            "
          ></v-select>
        </div>

        <SchoolSearchBar
          v-if="
            dealerMode &&
            isPlaybook &&
            !Object.keys(temporaryNewSchool).length &&
            selectedInstitutionIsSchool
          "
          :schoolKeyword="schoolKeyword"
          :selectedSchool="selectedSchool"
          v-on="{
            onSchoolSelected: handleSchoolSelected,
            input: handleUpdateSchoolKeyword,
            openCreateSchoolModal: handleOpenCreateSchoolModal,
          }"
        />

        <label
          v-else
          for="edit-team-name"
          class="input-container edit-team-name"
          :class="{ error: errors.teamName }"
        >
          <div class="sidebar-option-title">
            <span>Name</span>
          </div>
          <input
            id="edit-team-name"
            class="input-style"
            type="text"
            v-model="team_name"
            @input="
              debounceUpdate({
                dbKey: 'team_name',
                field: 'teamName',
                value: $event.target.value,
              })
            "
          />
          <span class="error-msg" v-if="errors.teamName">Missing Name</span>
        </label>
      </article>

      <!-- Team Name -->
      <article
        v-if="
          selectedInstitutionIsSchool &&
          dealerMode &&
          (isPlaybook || showAdditionalOptionInSidebar)
        "
        id="project-team-name"
      >
        <label
          for="edit-team-name"
          class="input-container"
          :class="{ error: errors.teamName }"
        >
          <div class="sidebar-option-title">
            <span>Team name</span>
          </div>
          <input
            id="edit-team-name"
            class="input-style"
            type="text"
            v-model="team_name"
            @input="
              debounceUpdate({
                dbKey: 'team_name',
                field: 'teamName',
                value: $event.target.value,
              })
            "
          />
          <span class="error-msg" v-if="errors.teamName"
            >Missing Team Name</span
          >
        </label>
      </article>

      <!-- Color Picker -->
      <article
        id="color-picker-sidebar"
        class="input-container"
        :class="{ error: errors.color_1 || errors.color_2 }"
      >
        <div class="sidebar-option-title">
          <span>Clients colors</span>
        </div>
        <ColorPicker
          :colorPickerMode="colorPickerMode"
          :colorsToChange="sidebarColors"
          @selectColor="handleSelectColor"
        />
        <span class="error-msg" v-if="errors.color_1 || errors.color_2"
          >Missing Colors</span
        >
      </article>

      <!--Graphics -->
      <article id="select-logos">
        <div class="sidebar-option-title">My Graphics</div>
        <SidebarLogos />
      </article>

      <!-- Catalogs selection -->
      <article class="sport-cards" v-if="dealerMode && showSportsCards">
        <div class="sidebar-option-title">
          <span>Add Sport</span>
        </div>
        <SportCard
          v-for="(sport, i) in sportsCards"
          :key="i + '-sportcard'"
          :sport="sport"
          :selectedSports="selectedSports"
          :disabledSports="disabledSports"
          :isSidebarOpen="isSidebarOpen"
          v-on="{ selectSport: handleSelectSport }"
        />
      </article>

      <article class="sport-cards" v-if="dealerMode">
        <div class="sidebar-option-title">
          <span>Add Categories</span>
        </div>
        <SportCard
          v-for="(sport, i) in categoriesCards"
          :key="i + '-sportcard'"
          :sport="sport"
          :selectedSports="selectedSports"
          :disabledSports="disabledSports"
          :isSidebarOpen="isSidebarOpen"
          v-on="{ selectSport: handleSelectSport }"
        />
      </article>

      <!-- Gender Selection -->
      <article id="gender-selection" v-if="isSnapsheet">
        <div class="sidebar-option-title">{{ genderSelectionTitle }}</div>
        <!-- Snapsheets -->
        <template v-if="isSnapsheet">
          <div class="radio-buttons-container">
            <label
              :for="gender.id"
              v-for="(gender, index) in featureGenders"
              :key="index + '-f-gender'"
            >
              <input
                type="radio"
                name="featured-gender"
                :id="gender.id"
                :value="gender.value"
                :checked="snapsheetGender === gender.value"
                @change="handleFeaturedGender(gender.value)"
              />
              <span>{{ gender.label }}</span>
            </label>
          </div>
        </template>
      </article>

      <!-- Show Prices toggle -->
      <article v-if="dealerMode" id="show-prices-container">
        <div class="sidebar-option-title slate-bold">Catalog Settings</div>

        <!-- Switch show prices -->
        <label class="switch-btn">
          <input
            type="checkbox"
            :checked="showProjectPrices"
            @change="updateShowPrices($event.target.checked)"
          />
          <div class="slide"></div>
          <span>Show Prices</span>
        </label>
      </article>

      <!-- Playbooks: Checkbox buttons to show Stock/Sublimation/All -->
      <article v-if="isPlaybook && dealerMode" class="product-filter-row">
        <div class="sidebar-option-title">Product Selection</div>
        <div
          class="filter-checkboxes-containter"
          v-if="productTypeFilter.length"
        >
          <label
            v-for="(type, index) in playbookTypeOfProductsFilterOptions"
            :for="'p-s-' + type.id"
            :key="index + '-p-s'"
          >
            <input
              type="checkbox"
              :name="'p-s-' + type.id"
              :id="'p-s-' + type.id"
              :value="type.value"
              :checked="productTypeFilter.includes(type.value)"
              @change="
                updateProductsFilter({
                  filterName: 'productTypeFilter',
                  filterValue: type.value,
                  dbKey: 'product_selection_filter',
                })
              "
            />
            <span>{{ type.label }}</span>
          </label>
        </div>
      </article>

      <!-- Gender filter -->
      <article v-if="isPlaybook && dealerMode" class="product-filter-row">
        <div class="sidebar-option-title">Gender</div>
        <div
          class="filter-checkboxes-containter"
          v-if="productGenderFilter.length"
        >
          <label
            v-for="(gender, index) in playbookGenderFilterOptions"
            :for="'gr-f-' + gender.value"
            :key="index + '-gr-f'"
          >
            <input
              type="checkbox"
              :name="'gr-f-' + gender.value"
              :id="'gr-f-' + gender.value"
              :value="gender.value"
              :checked="productGenderFilter.includes(gender.value)"
              :disabled="
                productGenderFilter.length === 1 &&
                gender.value === productGenderFilter[0]
              "
              @change="
                updateProductsFilter({
                  filterName: 'productGenderFilter',
                  filterValue: gender.value,
                  dbKey: 'gender_filter',
                })
              "
            />
            <span>{{ gender.label }}</span>
          </label>
        </div>
      </article>

      <article v-if="dealerMode">
        <!-- Switch show cover & index -->
        <label class="switch-btn">
          <input
            type="checkbox"
            :checked="showCoverAndIndex"
            @change="
              debounceUpdate({
                dbKey: 'show_cover_and_index',
                field: 'showCoverAndIndex',
                value: !showCoverAndIndex ? 1 : 0,
              })
            "
          />
          <div class="slide"></div>
          <span>Include Cover & Index</span>
        </label>
      </article>
    </template>

    <!-- Closed  -->
    <template v-else>
      <div id="closed-sidebar-icons">
        <!-- Colors -->
        <article class="selected-colors">
          <ColorCircle
            v-for="(color, i) in sidebarColors"
            :key="'closed-sidebar-color-' + i"
            :color="color.color"
            :readOnly="true"
            @click.native="$emit('openSidebar')"
          />
        </article>
        <!-- logos -->
        <article class="logos" v-if="projectLogos && projectLogos.length">
          <SidebarLogoPreview
            v-if="defaultLogo"
            :key="'default-logo-sb-closed'"
            :logo="defaultLogo"
            :isSidebarOpen="isSidebarOpen"
          />
          <SidebarLogoPreview
            v-for="(logo, i) in otherLogos"
            :key="'logo-sb-closed' + i"
            :logo="logo"
            :isSidebarOpen="isSidebarOpen"
          />

          <AddAnotherItem
            v-if="projectLogos.length"
            @openSidebar="$emit('openSidebar')"
          />
        </article>
        <!-- Catalogs -->
        <!-- Catalogs selection -->
        <article class="sport-cards-cls" v-if="dealerMode">
          <SportCard
            v-for="(sport, i) in catalogsCards"
            :key="i + '-sportcard'"
            :sport="sport"
            :selectedSports="selectedSports"
            :isSidebarOpen="isSidebarOpen"
            v-on="{ selectSport: handleSelectSport }"
          />
          <AddAnotherItem
            v-if="selectedSports.length"
            @openSidebar="$emit('openSidebar')"
          />
        </article>
      </div>
    </template>

    <transition name="fade">
      <CreateSalesRepModal
        v-if="showCreateSalesRepModal"
        :isAboveAll="true"
        :darker="true"
        :prepopulatedName="repsDropdownSearch"
        @close="showCreateSalesRepModal = !showCreateSalesRepModal"
      />
    </transition>
    <transition name="fade">
      <CreateSchoolModal
        v-if="showCreateSchoolModal"
        :isAboveAll="true"
        :darker="true"
        @close="showCreateSchoolModal = !showCreateSchoolModal"
      />
    </transition>
  </section>
</template>

<script>
import { debounce } from "debounce";

import { mapState, mapGetters, mapActions } from "vuex";
import CreateSalesRepModal from "@/components/Projects/Project/Sidebar/CreateSalesRepModal.vue";
import CreateSchoolModal from "@/components/Projects/Project/Sidebar/CreateSchoolModal.vue";
import AddAnotherItem from "@/components/Projects/Project/Sidebar/AddAnotherItem";
import ColorPicker from "@/components/Projects/Project/Colors/ColorPicker.vue";
import ColorCircle from "@/components/Projects/Project/Colors/ColorCircle.vue";
import InformationHover from "@/components/Projects/Project/InformationHover";
import SportCard from "@/components/Projects/Project/Sidebar/SportCard";
import SidebarLogos from "@/components/Projects/Project/Sidebar/SidebarLogos";
import SidebarLogoPreview from "@/components/Projects/Project/Sidebar/SidebarLogoPreview";
import SchoolSearchBar from "@/components/Dashboard/CreateNewProject/SchoolSearchBar.vue";
import CONSTANTS from "@/constants.js";

export default {
  name: "ProjectSidebar",
  components: {
    CreateSalesRepModal,
    CreateSchoolModal,
    AddAnotherItem,
    ColorPicker,
    ColorCircle,
    InformationHover,
    SportCard,
    SidebarLogos,
    SidebarLogoPreview,
    SchoolSearchBar,
  },
  props: {
    errors: {
      type: Object,
    },
    isSidebarOpen: {
      type: Boolean,
    },
    isSearchModalOpen: {
      type: Boolean,
    },

    sidebarHasErrors: {
      type: Boolean,
    },
  },

  data() {
    return {
      selectedRepId: "",
      schoolKeyword: "",
      repsDropdownSearch: "",
      showCreateSalesRepModal: false,
      showCreateSchoolModal: false,
      title: "",
      team_name: "",
      selectedSchool: "",
      featureGenders: [
        {
          id: "f-g-men",
          label: "Men",
          value: "Men",
        },
        {
          id: "f-g-youth",
          label: "Youth",
          value: "Youth",
        },
        {
          id: "f-g-women",
          label: "Women",
          value: "Women",
        },
        {
          id: "f-g-girls",
          label: "Girls",
          value: "Girls",
        },
      ],
      playbookTypeOfProductsFilterOptions: [
        {
          id: 0,
          label: "5 Day",
          value: "Five Day",
        },
        {
          id: 1,
          label: "Sublimation",
          value: "Sublimated",
        },
        {
          id: 2,
          label: "Stock",
          value: "Stock",
        },
      ],
      playbookGenderFilterOptions: [
        {
          id: 0,
          label: "Men's",
          value: "men",
        },
        {
          id: 1,
          label: "Women's",
          value: "women",
        },
      ],
      selectedCustomerType: "",
    };
  },

  computed: {
    ...mapState("project", [
      "typeOfProject",
      "projectLogos",
      "defaultLogoId",
      "temporaryNewSchool",
    ]),
    ...mapState("auth", ["dealerMode"]),
    ...mapState("catalog", ["projectCatalogs", "catalogsData"]),

    ...mapState("playbook", {
      productTypeFilter(state) {
        return state.productTypeFilter;
      },
      productGenderFilter(state) {
        return state.productGenderFilter;
      },
      customerType(state) {
        return state.customerType;
      },
      showCoverAndIndex(state) {
        return state.showCoverAndIndex;
      },
    }),

    ...mapState("snapsheet", {
      snapsheetCollectionsCards(state) {
        return state.collectionsCardsData;
      },
      snapsheetCollections(state) {
        return state.snapsheetCollections;
      },
      snapsheetGender(state) {
        return state.gender;
      },
    }),

    ...mapState("dealer", ["reps"]),
    ...mapState(["loading"]),

    ...mapGetters("project", [
      "projectColors",
      "projectRep",
      "projectTitle",
      "defaultLogo",
      "getColorByName",
      "isEditMode",
      "isCreateMode",
      "isPlaybook",
      "isSnapsheet",
      "isShowroom",
      "isTemporalProductEmpty",
      "projectTeamName",
      "showProjectPrices",
    ]),

    ...mapGetters("catalog", ["catalogsList"]),
    ...mapGetters("playbook", ["isSchool", "isTeam", "playbookPages"]),
    ...mapGetters("snapsheet", ["showAdditionalOptionInSidebar"]),

    institutions() {
      return CONSTANTS.PROJECT_INSTITUTION_NAMES;
    },
    selectedInstitutionIsSchool() {
      return this.selectedCustomerType === "school";
    },

    otherLogos() {
      return this.projectLogos?.filter(
        (logo) => logo?.id !== this.defaultLogoId
      );
    },

    colorPickerMode() {
      return this.isPlaybook ||
        this.isShowroom ||
        this.showAdditionalOptionInSidebar
        ? "multiple"
        : "single";
    },

    sidebarColors() {
      if (this.isSnapsheet) {
        return this.showAdditionalOptionInSidebar
          ? this.projectColors
          : [this.projectColors?.[0]];
      } else {
        return this.projectColors;
      }
    },

    catalogsCards() {
      return this.isPlaybook
        ? this.catalogsList
        : this.snapsheetCollectionsCards;
    },

    sportsCards() {
      return this.catalogsCards?.filter((catalog) => catalog?.type === "sport");
    },

    categoriesCards() {
      return this.catalogsCards?.filter(
        (catalog) => catalog?.type === "category"
      );
    },

    showSportsCards() {
      return this.isTeam || this.isSchool;
    },

    selectedSports() {
      return this.projectCatalogs.filter((catalog) => {
        //Search if the project has pages with the catalog
        return this.playbookPages.some(
          (page) =>
            page.catalog &&
            page.catalog.toLowerCase() === catalog.name?.toLowerCase()
        );
      });
    },

    disabledSports() {
      if (this.productGenderFilter.length === 2) {
        return [];
      }

      return this.catalogsCards.filter((catalog) => {
        //Search if the catalog has product that matches with the project's genders filter
        const catalogName = catalog?.name?.toLowerCase();

        const catalogProducts = this.catalogsData?.filter((pr) => {
          return pr?.catalogName?.toLowerCase() === catalogName;
        });

        const productsGenders = catalogProducts?.map((c) =>
          c.productGender?.toLowerCase()?.split(",")
        );
        console.log({ productsGenders });
        return !productsGenders.some((prGender) =>
          prGender.includes(this.productGenderFilter[0])
        );
      });
    },

    sportsSelectionTitle() {
      return this.isPlaybook ? "Sport Selection" : "Choose categories";
    },

    genderSelectionTitle() {
      return this.isPlaybook ? "Gender" : "Featured Gender";
    },
    typeOfProjectDisplay() {
      return this.isSnapsheet ? "Snap Sheet" : "Custom Catalog";
    },

    zIndex() {
      return this.showCreateSalesRepModal || this.showCreateSchoolModal;
    },
  },

  methods: {
    ...mapActions("catalog", ["getProductsCatalogs"]),
    ...mapActions("snapsheet", [
      "addSnapsheetCollection",
      "updateSnapsheetColor",
      "updateSnapsheetSettings",
      "updateSnapsheetGender",
    ]),
    ...mapActions("playbook", [
      "addSportToPlaybook",
      "updateProductsFilter",
      "updatePlaybook",
      "updatePlaybookColor",
      "updatePlaybookBySchool",
    ]),
    ...mapActions("project", [
      "endLoading",
      "showSaveIndicator",
      "setEditMode",
      "setPreviewMode",
      "triggerLoading",
      "updateDecorationsForProjectProductsLayouts",
      "updateShowPrices",
    ]),

    async handleSelectColor({ color, colorKey }) {
      try {
        this.triggerLoading();
        this.showSaveIndicator();
        if (this.isPlaybook) {
          /*let payload = {
            dbKey: colorKey,
            field: colorKey,
            value: color.id,
            skipLoader: true,
          };*/

          //Trigger a bulk update to change
          //color_1 -> the Main/Variant color
          //color_2 -> Decorations Fill
          //color_3 -> Decorations Outline

          await this.updatePlaybookColor({ colorId: color?.id, colorKey });
          //await this.updatePlaybook(payload);
        } else if (this.isSnapsheet) {
          await this.updateSnapsheetColor({ colorId: color?.id, colorKey });
        }

        this.emitRevalidate();
      } catch (error) {
        console.log(error);
      } finally {
        this.updateDecorationsForProjectProductsLayouts();
        this.endLoading();
      }
    },

    async handleSelectSport(sport) {
      try {
        this.triggerLoading();
        this.showSaveIndicator();
        if (this.isPlaybook) {
          await this.addSportToPlaybook({
            sport,
          });
        } else if (this.isSnapsheet) {
          await this.addSnapsheetCollection(sport.slug);
          await this.updateDecorationsForProjectProductsLayouts();
        }
        await this.updateDecorationsForProjectProductsLayouts();
      } catch (error) {
        console.log(error);
      } finally {
        this.endLoading();
      }
    },

    async handleFeaturedGender(selectedGender) {
      try {
        await this.updateSnapsheetGender(selectedGender);
      } catch (error) {
        console.log(error);
      }
    },

    toggleSidebar() {
      if (this.isSidebarOpen) {
        this.$emit("closeSidebar");
        // this.setPreviewMode();
      } else {
        this.$emit("openSidebar");
        this.dealerMode && this.setEditMode();
      }
    },

    emitRevalidate() {
      if (this.sidebarHasErrors) this.$emit("validateProjectSettings");
    },

    debounceUpdate: debounce(async function ({ dbKey, field, value }) {
      console.log({ dbKey, field, value });
      if (field === "team_name" && value?.trim() === "") value = "Team name";

      let payload = {
        dbKey,
        field,
        value,
        skipLoader: true,
      };

      //Update the component state
      this[field] = value;

      //Update in DB
      try {
        this.triggerLoading();
        this.showSaveIndicator();
        if (this.isPlaybook) {
          await this.updatePlaybook(payload);
        } else if (this.isSnapsheet) {
          await this.updateSnapsheetSettings(payload);
        }
        this.emitRevalidate();
      } catch (error) {
        console.log(error);
      } finally {
        this.updateDecorationsForProjectProductsLayouts();
        this.endLoading();
      }
    }, 500),

    /* School search bar /*/

    //Handle form updates
    async handleSchoolSelected(school) {
      this.triggerLoading();

      /*let payload = {
        dbKey,
        field,
        value,
        skipLoader: true,
      };*/

      try {
        this.showSaveIndicator();
        if (this.isPlaybook) {
          ////updatePlaybookColor???? bulk
          ///update c1, c2, c3 and mascot team name

          let params = {
            mascot: school.mascot,
            schoolName: school.name,
          };

          if (school.color_1)
            params.color_1 = this.getColorByName(school.color_1)?.id;
          if (school.color_2)
            params.color_2 = this.getColorByName(school.color_2)?.id;
          if (school.color_3)
            params.color_3 = this.getColorByName(school.color_3)?.id;
          if (school.school_code) params.schoolCode = school.school_code;
          if (school.logos_ids?.length > 0) params.logosIds = school.logos_ids;

          await this.updatePlaybookBySchool(params);
          await this.updateDecorationsForProjectProductsLayouts();

          //    await this.updatePlaybook(payload);
        }
        this.emitRevalidate();
        this.schoolKeyword = school.complete_name;
      } catch (error) {
        console.log(error);
      } finally {
        this.endLoading();
      }
    },
    handleSetSchool(school) {
      this.selectedSchool = school;
      this.handleUpdateSchoolKeyword(school.name);
      this.handleUpdateTeamName(school.name);
      this.handleSelectColor({
        field: "color_1",
        color: this.getColorByName(school.color_1),
      });
      this.handleSelectColor({
        field: "color_2",
        color: this.getColorByName(school.color_2),
      });
      this.handleSelectColor({
        field: "color_3",
        color: this.getColorByName(school.color_3),
      });
    },
    handleOpenCreateSchoolModal() {
      this.showCreateSchoolModal = true;
    },
    handleUpdateSchoolKeyword(value) {
      this.schoolKeyword = value;
    },
    handleUpdateTeamName(value) {
      this.project_title = value;
    },
  },

  mounted() {
    if (this.isCreateMode) {
      this.$emit("openSidebar");
    }

    this.title = this.projectTitle;
    this.selectedRepId = this.projectRep?.id;
    this.team_name = this.projectTeamName;
    this.selectedCustomerType = this.customerType;
  },

  watch: {
    dealerMode() {
      if (this.dealerMode) {
        this.selectedRepId = this.projectRep?.id;
        this.title = this.projectTitle;
        this.team_name = this.projectTeamName;
      }
    },
    loading() {
      if (!this.loading) {
        this.selectedRepId = this.projectRep?.id;
        this.title = this.projectTitle;
        this.team_name = this.projectTeamName;
        this.selectedCustomerType = this.customerType;
      }
    },
    async selectedCustomerType(newValue, oldValue) {
      const wasSchoolOrTeam = oldValue === "school" || oldValue === "team";
      const newValueIsNotSchoolOrTeam =
        newValue !== "school" && newValue !== "team";

      //When the user change the customer type from school/team to a not-school/not-team
      //Delete the sports catalogs that were selected
      if (oldValue && wasSchoolOrTeam && newValueIsNotSchoolOrTeam) {
        const sportCatalogs = this.projectCatalogs?.filter(
          (catalog) => catalog?.type === "sport"
        );

        if (sportCatalogs?.length) {
          try {
            for (const catalog of sportCatalogs) {
              await this.addSportToPlaybook({ sport: catalog });
              this.selectedCustomerType = newValue;
            }
          } catch (error) {
            console.log(error);
          }
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
* {
  transition: all 0.3s ease-in-out;
}
#project-sidebar {
  background: $grey-lightest;
  display: flex;
  flex-direction: column;
  padding: 1.6rem 1.5rem;
  padding-top: 0;
  top: 0;
  left: 0;
  bottom: 0;
  transition: all 0.3s ease-in-out;
  width: 6rem;
  z-index: 10;

  * {
    font-size: 16px;
  }

  & > * {
    margin-bottom: 2.5rem;
  }

  &.z-index-full {
    z-index: 100;
  }

  &.opened {
    width: 416px;
    overflow-y: auto;
    overflow-x: visible;

    .sidebar-option-title,
    ::v-deep .sidebar-option-title {
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      text-transform: capitalize;
      width: 100%;
      max-height: 24px;
      span:first-child {
        margin-right: 0.25rem;
      }
      span.grey {
        color: $grey-dark;
        margin-right: 0.5rem;
      }
    }

    article > label {
      width: 100%;
    }

    .sidebar-header {
      background: $grey-lightest;
      display: flex;
      justify-content: space-between;
      margin-bottom: 1rem;
      position: sticky;
      padding-top: 1.6rem;
      padding-bottom: 0.5rem;
      top: 0;
      width: 100%;
      z-index: 10;
      .sidebar-title {
        text-transform: capitalize;
        color: $placeholder;
      }
      button {
        transform: rotate(180deg);
      }
    }

    .input-container,
    ::v-deep .input-container {
      &.error {
        label {
          color: $redError;
        }
        input,
        textarea,
        .input-style {
          border: 2px solid $redError;
        }

        span {
          color: $redError;
        }

        span.error-msg {
          color: $redError;
          font-size: 0.8em;
          margin-top: 0.25rem;
          text-align: left;
        }
      }
    }

    .input-style {
      width: 100%;
      padding: 0.75rem;
      background: white;
      border: 1px solid $grey-light;
      border-radius: 6px;
      height: 3.1rem;
      text-transform: capitalize !important;
      font-family: $slateBold;

      &.select {
        padding: 0;
        border: none;
        ::v-deep {
          .vs__dropdown-toggle {
            border-radius: 6px;
            border: 1px solid $grey-light;
            padding: 0.5rem;
            height: 3.1rem;
            span {
              font-family: $slateBold !important;
            }
          }
        }
      }
    }

    #sales-rep {
      ::v-deep {
        ul .new-rep-option {
          button {
            align-items: center;
            border-bottom: 1px solid $borderColor;
            display: flex;
            font-weight: bold;
            padding: 0.8rem 0.75rem;
            width: 100%;
            span {
              margin-right: 1rem;
            }
            img {
              width: 1.2rem;
              height: 1.2rem;
            }
            &:hover {
              transform: initial;

              span {
                text-decoration: underline;
              }
            }
          }
        }
      }
    }

    #customer-type {
      display: flex;
      justify-content: space-between;

      .customer-type-dropdown {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        width: 37%;

        .sidebar-option-title {
          margin: 0;
        }

        #institution-select {
          display: flex;
          width: 100%;
          ::v-deep * {
            text-transform: capitalize !important;
          }
          ::v-deep .vs__dropdown-toggle {
            padding: 0;
            width: 100%;
          }
          ::v-deep .vs__selected {
            font-size: 1rem;
            text-transform: capitalize;
          }
        }
      }

      form,
      .edit-team-name {
        width: 60%;
      }

      .input-container {
        margin-bottom: 0;
      }
    }

    .sport-cards {
      display: flex;
      flex-wrap: wrap;
      column-gap: 0.8rem;
      row-gap: 1rem;
      .manual-selection {
        margin-top: 2rem;
        width: 100%;
        &.search-open {
          button {
            color: $green;
          }
        }
        button {
          margin-left: 0.5rem;
          background: none;
          text-decoration: underline;
        }

        @media screen and (max-width: 1024px) {
          justify-content: space-evenly;
        }
      }
    }

    #show-prices-container {
      margin-bottom: 1rem;
    }

    .product-filter-row {
      display: flex;
      width: 100%;
      margin-bottom: 1rem;

      .sidebar-option-title {
        width: fit-content;
        margin-right: 1rem;
        width: 20%;
      }
      .filter-checkboxes-containter {
        display: flex;
        justify-content: flex-start;
        flex-wrap: nowrap;
        width: 80%;
        height: 30px;

        label {
          margin-right: 0.5rem;
          width: auto;
        }
      }
    }

    .radio-buttons-container,
    .filter-checkboxes-containter {
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      align-items: center;
      gap: 1rem;
      label {
        cursor: pointer;
        width: 40%;
        margin: 0;
        display: flex;
        align-items: center;
        span {
          margin-left: 0.5rem;
        }
      }

      input[type="radio"] {
        appearance: none;
        background-color: #fff;
        cursor: pointer;
        margin: 0;
        font: inherit;
        color: currentColor;
        width: 1.2em;
        height: 1.2em;
        border: 0.15em solid currentColor;
        border-radius: 50%;
        /* transform: translateY(-0.075em); */
        display: grid;
        place-content: center;

        &::before {
          content: "";
          width: 0.65em;
          height: 0.65em;
          border-radius: 50%;
          transform: scale(0);
          transition: 120ms transform ease-in-out;
          box-shadow: inset 1em 1em $playbookColor;
        }
        &:checked,
        &:hover {
          border: 0.15em solid $playbookColor;
        }
        &:checked::before {
          transform: scale(1);
        }
        &:focus {
          outline: max(2px, 0.15em) solid currentColor;
          outline-offset: max(2px, 0.15em);
        }
      }
    }

    @media screen and (max-width: 1024px) {
      width: 35%;
      padding: 1rem;
      padding-top: 0;
    }

    @media screen and (max-width: 576px) {
      width: 75%;
      padding: 1rem;
      padding-top: 0;
    }
  }

  &.closed {
    display: flex;
    flex-direction: column;
    align-items: center;
    .sidebar-header {
      align-items: center;
      display: flex;
    }
    #closed-sidebar-icons {
      display: flex;
      flex-direction: column;
      align-items: center;

      & > article {
        margin-bottom: 2rem;
      }

      .selected-colors {
        ::v-deep .color-circle {
          margin-bottom: 1.2rem;
          cursor: pointer;
        }
      }
    }

    @media screen and (max-width: 576px) {
      left: -6rem;
    }
  }

  &.edit-mode {
    position: fixed;
    z-index: 100;
    border: 1px solid $grey-light;
    box-shadow: 8px 2px 1.5rem rgba(36, 36, 36, 0.03);
  }

  .sidebar-header {
    position: relative;
    #toggle-sidebar {
      height: 26px;
      width: 26px;
      background: none;
      &.close {
        margin: auto;
        margin-top: 1.6rem;
        display: block;
        position: initial;
      }
    }
  }

  .switch-btn {
    position: relative;
    display: flex;

    align-items: center;
    width: 100%;
    height: 34px;
  }
  .switch-btn input {
    display: none;
  }
  .slide {
    display: flex;
    align-items: center;
    border-radius: 34px;
    position: relative;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: $switchBackground;
    border: 1px solid $switchBorder;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    margin-right: 1rem;
    padding: 7px 4px !important;
    height: 10px;
    color: #fff;
    width: 30px !important;
  }
  .slide:before {
    border-radius: 50%;
    position: absolute;
    content: "";
    height: 17px;
    width: 17px;
    left: 0;
    bottom: -1px;
    background-color: $grey-dark;
    -webkit-transition: 0.4s;
    transition: 0.4s;
  }
  input:checked + .slide {
    background-color: $switchBackground;
    padding-right: 40px;

    &:before {
      background-color: $switchOn;
    }
  }
  input:focus + .slide {
    box-shadow: 0 0 1px #01aeed;
  }
  input:checked + .slide:before {
    -webkit-transform: translateX(14px);
    -ms-transform: translateX(14px);
    transform: translateX(14px);
    right: 0px;
  }
}
</style>
